import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { getCurrentLocation, requestLocationPermission, hasLocationPermission } from '../utils/location'

const LoginPage = () => {
  const { login, verifyOTP } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('admin@example.com')
  const [password, setPassword] = useState('Admin@12345')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [locationGranted, setLocationGranted] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [requiresOTP, setRequiresOTP] = useState(false)
  const [mobile, setMobile] = useState('')
  const [smsSent, setSmsSent] = useState(false)
  const [hasMobile, setHasMobile] = useState(false)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const [remainingAttempts, setRemainingAttempts] = useState(5)
  const [location, setLocation] = useState(null)

  const currentYear = new Date().getFullYear()

  // Request location permission on mount
  useEffect(() => {
    const checkLocation = async () => {
      if (!hasLocationPermission()) {
        setLocationError('Location services are not available in this browser')
        return
      }

      try {
        // Try to get location to trigger permission prompt
        const coords = await getCurrentLocation()
        setLocation(coords)
        setLocationGranted(true)
        setLocationError('')
      } catch (err) {
        setLocationError('Location permission is required for login. Please enable location access and refresh the page.')
        setLocationGranted(false)
      }
    }

    checkLocation()
  }, [])

  const onSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    if (!locationGranted) {
      setError('Please grant location permission to continue')
      return
    }

    setLoading(true)
    try {
      // Get current location before submitting
      let currentLocation = location
      if (!currentLocation) {
        try {
          currentLocation = await getCurrentLocation()
          setLocation(currentLocation)
        } catch (err) {
          // If location fails, continue without it (will use IP)
          console.warn('Failed to get location:', err)
        }
      }

      const res = await login(email, password, currentLocation)
      setLoading(false)
      
      if (res.requiresOTP) {
        setRequiresOTP(true)
        setMobile(res.mobile || null)
        setSmsSent(res.smsSent || false)
        setHasMobile(res.hasMobile || false)
        setError('')
      } else if (res.success) {
        navigate('/dashboard')
      } else {
        setError(res.error || 'Login failed')
      }
    } catch (err) {
      setLoading(false)
      setError(err.message || 'Login failed')
    }
  }

  const onOTPSubmit = async (e) => {
    e.preventDefault()
    setOtpError('')
    
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP')
      return
    }

    setOtpLoading(true)
    try {
      // Get current location
      let currentLocation = location
      if (!currentLocation) {
        try {
          currentLocation = await getCurrentLocation()
          setLocation(currentLocation)
        } catch (err) {
          // Continue without location (will use IP)
          console.warn('Failed to get location:', err)
        }
      }

      const res = await verifyOTP(email, otp, currentLocation)
      setOtpLoading(false)
      
      if (res.success) {
        navigate('/dashboard')
      } else {
        setOtpError(res.error || 'Invalid OTP')
        if (res.remainingAttempts !== undefined) {
          setRemainingAttempts(res.remainingAttempts)
        }
      }
    } catch (err) {
      setOtpLoading(false)
      setOtpError(err.message || 'OTP verification failed')
    }
  }

  const handleResendOTP = async () => {
    setOtpError('')
    setOtp('')
    setLoading(true)
    try {
      let currentLocation = location
      if (!currentLocation) {
        try {
          currentLocation = await getCurrentLocation()
          setLocation(currentLocation)
        } catch (err) {
          console.warn('Failed to get location:', err)
        }
      }

      const res = await login(email, password, currentLocation)
      setLoading(false)
      
      if (res.requiresOTP) {
        setMobile(res.mobile || null)
        setSmsSent(res.smsSent || false)
        setHasMobile(res.hasMobile || false)
        setRemainingAttempts(5)
      } else {
        setError(res.error || 'Failed to resend OTP')
      }
    } catch (err) {
      setLoading(false)
      setError(err.message || 'Failed to resend OTP')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo and Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-lg mb-4">
            <span className="text-white font-bold text-2xl">VE</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h1>
          <p className="text-gray-600">Sign in to your VideoEditor account</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8">
          {/* Location Permission Warning */}
          {!locationGranted && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">Location Permission Required</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    {locationError || 'Please allow location access to continue with login. This is required for security purposes.'}
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const coords = await getCurrentLocation()
                        setLocation(coords)
                        setLocationGranted(true)
                        setLocationError('')
                      } catch (err) {
                        setLocationError('Failed to get location. Please check your browser settings and try again.')
                      }
                    }}
                    className="mt-2 text-xs text-yellow-800 underline hover:text-yellow-900"
                  >
                    Grant Permission
                  </button>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {!requiresOTP ? (
            <form onSubmit={onSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/50 backdrop-blur-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !locationGranted}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 focus:ring-4 focus:ring-blue-500/25 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
          ) : (
            <div className="space-y-6">
              {/* OTP Input Step */}
              <div className="text-center mb-4">
                {hasMobile && mobile ? (
                  <>
                    {smsSent ? (
                      <>
                        <p className="text-sm text-gray-600">
                          OTP sent to <span className="font-medium text-gray-800">{mobile}</span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Enter the 6-digit code sent to your mobile number
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-800">
                            Failed to send OTP
                          </p>
                          <p className="text-xs text-red-700 mt-1">
                            Please contact administrator for OTP
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Mobile: {mobile}
                        </p>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm font-medium text-yellow-800">
                        OTP Generated
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Please contact administrator to get your OTP code
                      </p>
                    </div>
                  </>
                )}
              </div>

              {otpError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-red-700">{otpError}</span>
                  </div>
                </div>
              )}

              <form onSubmit={onOTPSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setOtp(value)
                      setOtpError('')
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    required
                    autoFocus
                  />
                  {remainingAttempts < 5 && (
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setRequiresOTP(false)
                      setOtp('')
                      setOtpError('')
                      setRemainingAttempts(5)
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={otpLoading || otp.length !== 6}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium py-2 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 focus:ring-4 focus:ring-blue-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {otpLoading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Sending...' : 'Resend OTP'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Links */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
              <Link 
                to="/register" 
                className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
              >
                Create new account
              </Link>
              <Link 
                to="/forgot-password" 
                className="text-sm text-gray-600 hover:text-gray-700 transition-colors duration-200"
              >
                Forgot password?
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            © {currentYear} VideoEditor. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage


